import { useMemo, useState, type RefObject } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { toast } from "@/components/shared/toast";
import { getErrorMessage } from "@/lib/api-client";
import { downloadAppAnalysisRunLogs } from "@/lib/api/app-analysis";
import { appAnalysisRunLogsInfiniteQueryOptions } from "@/features/app-analysis/api";
import { RunLogsDrawer, RunLogsList, type RunLogLevel } from "@/components/shared/RunLogsView";
import { formatMeasurementLogMessage } from "@/lib/qlik-evaluation-format";

// The analysis's worker execution-log stream, bound to the app-analysis log
// source. Presentation lives in the shared RunLogsList; only the data wiring
// (query + export) is app-analysis-specific.
export function AppAnalysisLogsList({
  analysisId,
  active,
}: {
  analysisId: string;
  active: boolean;
}) {
  const { t } = useTranslation("analyses");
  const [level, setLevel] = useState<RunLogLevel>("info");

  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      appAnalysisRunLogsInfiniteQueryOptions(
        analysisId,
        { level: level === "info" ? undefined : level },
        active,
      ),
    );

  const rows = useMemo(
    () =>
      data?.pages.flatMap((p) =>
        p.data.map((row) => ({
          ...row,
          message: formatMeasurementLogMessage(row.message, row.attrs, t),
        })),
      ) ?? [],
    [data, t],
  );

  return (
    <RunLogsList
      ns="analyses"
      rows={rows}
      isLoading={isLoading}
      isError={isError}
      active={active}
      level={level}
      onLevelChange={setLevel}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onLoadMore={() => {
        if (hasNextPage && !isFetchingNextPage) fetchNextPage();
      }}
      onExport={() =>
        downloadAppAnalysisRunLogs(analysisId).catch((err) =>
          toast.error(getErrorMessage(err, t("logs.exportFailed"))),
        )
      }
    />
  );
}

export function AppAnalysisLogsDrawer({
  open,
  onOpenChange,
  analysisId,
  active,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  analysisId: string;
  active: boolean;
  /** The button that opened the drawer: focus goes back to it on close. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}) {
  return (
    <RunLogsDrawer
      open={open}
      onOpenChange={onOpenChange}
      ns="analyses"
      restoreFocusRef={restoreFocusRef}
    >
      <AppAnalysisLogsList analysisId={analysisId} active={active} />
    </RunLogsDrawer>
  );
}
